/**
 * Section Feedback Dialog
 *
 * Captures user feedback for regenerating / refining a proposal section.
 * Two modes (same component, backward compatible):
 *  - 'enhance' (default): the legacy "Enhance Section" flow in the workspace/preview.
 *  - 'refine': the checkpoint-gate "Refine with feedback" flow, which also surfaces
 *    the AI's "[DATA NEEDED]" gaps so the user can ask the AI to address them.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  X,
  Sparkles,
  RefreshCw,
  MessageSquare,
  Lightbulb,
  AlertTriangle,
  Plus,
  Wand2,
} from 'lucide-react';
import type { DataNeededItem } from '../../lib/section-content-utils';

// ============================================================================
// TYPES
// ============================================================================

interface SectionFeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedback: SectionFeedback) => void;
  sectionTitle: string;
  sectionType: string;
  isSubmitting?: boolean;
  // 'refine' tweaks the copy and shows the data-needed panel; defaults to 'enhance'
  // so the existing workspace/preview call sites are unchanged.
  mode?: 'enhance' | 'refine';
  // AI-flagged "[DATA NEEDED]" gaps for this section (refine mode).
  dataNeededItems?: DataNeededItem[];
}

export interface SectionFeedback {
  sectionType: string;
  userFeedback: string;
  enhancementLevel: 'minor' | 'moderate' | 'significant';
  specificInstructions?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function SectionFeedbackDialog({
  isOpen,
  onClose,
  onSubmit,
  sectionTitle,
  sectionType,
  isSubmitting = false,
  mode = 'enhance',
  dataNeededItems = [],
}: SectionFeedbackDialogProps) {
  const [feedback, setFeedback] = useState('');
  const [enhancementLevel, setEnhancementLevel] = useState<'minor' | 'moderate' | 'significant'>('moderate');

  const isRefine = mode === 'refine';

  // Reset the form whenever the dialog (re)opens so stale text never carries over.
  useEffect(() => {
    if (isOpen) {
      setFeedback('');
      setEnhancementLevel('moderate');
    }
  }, [isOpen, sectionType]);

  // Quick feedback suggestions based on section type.
  const suggestions = useMemo(() => {
    const baseSuggestions = [
      'Add more specific detail',
      'Make it more compelling',
      'Include concrete examples',
      'Tighten and clarify the language',
    ];
    const sectionSuggestions: Record<string, string[]> = {
      cover_letter: ['Emphasize our unique qualifications', 'Reference specific past contracts', 'Make the opening more engaging', 'Strengthen the closing'],
      executive_summary: ['Add more discriminators', 'Expand the value proposition', 'Tie in relevant experience', 'Sharpen the technical overview'],
      'technical-management': ['Add technical depth to the methodology', 'Name specific tools and standards', 'Expand the task breakdown', 'Map tasks to the PWS'],
      technical_approach: ['Add technical depth to the methodology', 'Name specific tools and standards', 'Expand the task breakdown', 'Add innovation examples'],
      management_approach: ['Detail the key roles', 'Expand the communication plan', 'Add risk mitigations', 'Detail the transition plan'],
      'pp-personnel': ['Highlight more achievements', 'Strengthen relevance to this contract', 'Add references', 'Expand key personnel qualifications'],
      past_performance: ['Highlight achievements', 'Strengthen relevance', 'Add references', 'Expand scope descriptions'],
      key_personnel: ['Add certifications detail', 'Expand relevant experience', 'Add specific qualifications', 'Note education'],
      'pricing-compliance': ['Clarify the rate methodology', 'Justify the labor mix', 'Expand the basis of estimate', 'Address the compliance matrix'],
      pricing: ['Clarify rate methodology', 'Add value detail', 'Expand cost breakdown', 'Note efficiencies'],
      compliance: ['Add compliance notes', 'List certifications', 'Expand acknowledgments', 'Detail clearances'],
      backbone: ['Reconsider the labor mix', 'Adjust the hours estimate', 'Re-scope a deliverable', 'Correct the contract vehicle assumption'],
    };
    const normalizedType = sectionType.toLowerCase().replace(/[^a-z_-]/g, '_');
    return sectionSuggestions[sectionType] || sectionSuggestions[normalizedType] || baseSuggestions;
  }, [sectionType]);

  const appendToFeedback = (text: string) => {
    setFeedback(prev => (prev.trim() ? `${prev.trim()}\n${text}` : text));
  };

  const addAllDataNeeded = () => {
    if (dataNeededItems.length === 0) return;
    const lines = dataNeededItems.map(d => `- ${d.field}: provide ${d.marker}`).join('\n');
    appendToFeedback(`Fill in the following flagged gaps using the contract and company data (do not leave placeholders):\n${lines}`);
  };

  const handleSubmit = () => {
    if (!feedback.trim()) return;
    onSubmit({ sectionType, userFeedback: feedback.trim(), enhancementLevel });
  };

  if (!isOpen) return null;

  const title = isRefine ? 'Refine with feedback' : 'Enhance Section';
  const Icon = isRefine ? Wand2 : Sparkles;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-bw-surface dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-bw-border dark:border-gray-700 bg-bw-surface-subtle dark:from-gray-800 dark:to-gray-750">
          <div className="flex justify-between items-start gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-bw-primary-tint dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-bw-primary dark:text-blue-400" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-bw-ink dark:text-white truncate">{title}</h2>
                <p className="text-sm text-bw-muted dark:text-bw-muted truncate">{sectionTitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="p-2 text-bw-muted hover:text-bw-muted dark:hover:text-gray-300 hover:bg-bw-surface-subtle dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content (scrolls) */}
        <div className="p-6 space-y-5 overflow-y-auto">
          {/* Data-needed gaps (refine mode) */}
          {isRefine && dataNeededItems.length > 0 && (
            <div className="rounded-xl border border-amber-300 bg-fit-moderate-bg dark:bg-amber-900/20 p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-fit-moderate" />
                  <span className="text-sm font-semibold text-fit-moderate dark:text-amber-200">
                    The AI flagged {dataNeededItems.length} gap{dataNeededItems.length === 1 ? '' : 's'} in this section
                  </span>
                </div>
                <button
                  type="button"
                  onClick={addAllDataNeeded}
                  disabled={isSubmitting}
                  className="text-xs font-medium text-fit-moderate bg-fit-moderate-bg hover:bg-amber-200 border border-amber-300 rounded-lg px-2.5 py-1 transition-colors disabled:opacity-50"
                >
                  Ask AI to address all
                </button>
              </div>
              <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                {dataNeededItems.map((d, i) => (
                  <li key={i} className="flex items-start justify-between gap-2 text-xs">
                    <span className="text-amber-900 dark:text-amber-200 min-w-0">
                      <span className="font-medium">{d.field}</span>{' '}
                      <span className="bg-amber-200/60 dark:bg-amber-800/40 rounded px-1 py-0.5">{d.marker}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => appendToFeedback(`${d.field}: provide ${d.marker}`)}
                      disabled={isSubmitting}
                      title="Add this to your feedback"
                      className="inline-flex items-center gap-0.5 text-fit-moderate hover:text-amber-900 font-medium flex-shrink-0 disabled:opacity-50"
                    >
                      <Plus className="w-3 h-3" /> add
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Feedback textarea */}
          <div>
            <label className="block text-sm font-medium text-bw-ink dark:text-gray-300 mb-2">
              <MessageSquare className="w-4 h-4 inline mr-2" />
              {isRefine ? 'What should the AI fix or change?' : 'Your feedback & instructions'}
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={
                isRefine
                  ? 'e.g. "This is an IDIQ, not a single-award buy — price a representative task order, not the ceiling."'
                  : "Tell the AI what you'd like to change, add, or improve…"
              }
              rows={5}
              disabled={isSubmitting}
              className="w-full px-4 py-3 border border-bw-border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-blue-500 dark:bg-gray-700 dark:text-white resize-y disabled:opacity-50 text-sm leading-relaxed"
            />
            {isRefine && (
              <p className="text-xs text-bw-muted dark:text-bw-muted mt-1.5">
                This is recorded as an <span className="font-medium">authoritative correction</span> applied to this step and every step after it, then this step regenerates.
              </p>
            )}
          </div>

          {/* Quick suggestions */}
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-bw-ink dark:text-gray-300 mb-2">
              <Lightbulb className="w-4 h-4 text-fit-moderate" />
              Quick suggestions
            </div>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => appendToFeedback(suggestion)}
                  disabled={isSubmitting}
                  className="px-3 py-1.5 text-xs bg-bw-surface-subtle dark:bg-gray-700 text-bw-ink dark:text-gray-300 rounded-full hover:bg-bw-primary-tint dark:hover:bg-blue-900/30 hover:text-bw-primary dark:hover:text-blue-300 transition-colors disabled:opacity-50"
                >
                  + {suggestion}
                </button>
              ))}
            </div>
          </div>

          {/* Enhancement / depth level */}
          <div>
            <label className="block text-sm font-medium text-bw-ink dark:text-gray-300 mb-3">
              <RefreshCw className="w-4 h-4 inline mr-2" />
              {isRefine ? 'How much should change?' : 'Enhancement level'}
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'minor', label: 'Light', description: 'Targeted fixes only' },
                { value: 'moderate', label: 'Moderate', description: 'Add detail & depth' },
                { value: 'significant', label: 'Heavy', description: 'Substantial rewrite' },
              ].map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setEnhancementLevel(option.value as typeof enhancementLevel)}
                  disabled={isSubmitting}
                  className={`p-3 rounded-lg border-2 transition-all text-left disabled:opacity-50 ${
                    enhancementLevel === option.value
                      ? 'border-blue-500 bg-bw-primary-tint dark:bg-blue-900/20'
                      : 'border-bw-border dark:border-gray-600 hover:border-bw-border dark:hover:border-gray-500'
                  }`}
                >
                  <div className={`font-medium text-sm ${
                    enhancementLevel === option.value ? 'text-bw-primary dark:text-blue-300' : 'text-bw-ink dark:text-white'
                  }`}>
                    {option.label}
                  </div>
                  <div className="text-xs text-bw-muted dark:text-bw-muted mt-0.5">{option.description}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-bw-surface-subtle dark:bg-gray-800/50 border-t border-bw-border dark:border-gray-700 flex justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-bw-ink dark:text-gray-300 bg-bw-surface dark:bg-gray-700 border border-bw-border dark:border-gray-600 rounded-lg hover:bg-bw-surface-subtle dark:hover:bg-gray-600 transition-colors font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!feedback.trim() || isSubmitting}
            className="px-4 py-2 bg-bw-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <><RefreshCw className="w-4 h-4 animate-spin" /> {isRefine ? 'Regenerating…' : 'Regenerating…'}</>
            ) : (
              <><Icon className="w-4 h-4" /> {isRefine ? 'Apply & regenerate' : 'Regenerate section'}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SectionFeedbackDialog;
